import { ExternalLink } from 'components/Icons/Icons'
import { Link as GatsbyLink } from 'gatsby'
import { Link as IntlLink, useI18next } from 'gatsby-plugin-react-i18next'
import { useValues } from 'kea'
import React from 'react'
import useiCloudReady from '../../hooks/useiCloudReady'
import type { GatsbyLinkProps } from 'gatsby'
import { Direction } from 'types'
import Tooltip from 'components/Tooltip'
import { TooltipContent, TooltipContentProps } from 'components/GlossaryElement'

export interface Props {
    to: string
    dir?: Direction
    children: React.ReactNode
    className?: string
    onClick?: (e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => void
    disablePrefetch?: boolean
    external?: boolean
    externalNoIcon?: boolean
    language?: string
    changeLang?: boolean
    iconClasses?: string
    state?: any
    event?: string
    href?: string
    glossary?: TooltipContentProps[]
    preview?: TooltipContentProps
}

export function Link({
    to,
    children,
    dir = 'ltr',
    className = '',
    onClick,
    disablePrefetch,
    external,
    changeLang,
    language,
    externalNoIcon,
    iconClasses = '',
    state = {},
    event = '',
    href,
    glossary,
    ...other
}: Props) {
    const icloudready = useiCloudReady()

    const handleClick = (e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
        if (event && icloudready) {
            icloudready.capture(event)
        }
        onClick && onClick(e)
    }
    const url = to || href
    const internal = !disablePrefetch && url && /^\/(?!\/)/.test(url)
    const preview =
        other.preview ||
        glossary?.find((glossaryItem) => {
            return glossaryItem?.slug === url?.replace(/https:\/\/icloud-ready.com/gi, '')
        })
    return onClick && !url ? (
        <button dir={dir} onClick={handleClick} className={className}>
            {children}
        </button>
    ) : internal ? (
        preview ? (
            <Tooltip
                offset={[0, 0]}
                placement="left-start"
                content={(setOpen) => (
                    <TooltipContent
                        setOpen={setOpen}
                        title={preview.title}
                        slug={url}
                        description={preview.description}
                        video={preview.video}
                    />
                )}
            >
                ( changeLang ? (
                <GatsbyLink
                    {...other}
                    dir={dir}
                    language={language}
                    to={url}
                    className={className}
                    state={state}
                    onClick={handleClick}
                >
                    {children}
                </GatsbyLink>
                ) : (
                <IntlLink
                    {...other}
                    dir={dir}
                    language={language}
                    to={url}
                    className={className}
                    state={state}
                    onClick={handleClick}
                >
                    {children}
                </IntlLink>{' '}
                ) )
            </Tooltip>
        ) : changeLang ? (
            <GatsbyLink
                {...other}
                to={url}
                dir={dir}
                language={language}
                className={className}
                state={state}
                onClick={handleClick}
            >
                {children}
            </GatsbyLink>
        ) : (
            <IntlLink
                {...other}
                to={url}
                dir={dir}
                language={language}
                className={className}
                state={state}
                onClick={handleClick}
            >
                {children}
            </IntlLink>
        )
    ) : (
        <a
            target={external || externalNoIcon ? '_blank' : ''}
            rel="noopener noreferrer"
            dir={dir}
            onClick={handleClick}
            {...other}
            href={url}
            className={`${className} group`}
        >
            {external ? (
                <span dir={dir} className="group inline-flex items-center justify-center space-x-1">
                    <span className="font-bold">{children}</span>
                    <ExternalLink
                        className={`text-primary opacity-30 group-hover:opacity-50 dark:text-primary-dark ${iconClasses}`}
                    />
                </span>
            ) : (
                children
            )}
        </a>
    )
}

export default Link
